<template>
  <div class="rpt-page">
    <div class="rpt-head">
      <div>
        <div class="rpt-crumb">Reportes</div>
        <h2 class="rpt-title">Rechazos</h2>
        <p class="rpt-subtitle">
          Facturas denegadas en el periodo, con su motivo registrado y qué
          laboratorios concentran los rechazos. La tasa compara contra lo
          recibido en la misma ventana, no contra el histórico completo.
        </p>
      </div>
      <div class="rpt-actions">
        <v-btn
          icon
          to="/reportes/guia"
          title="Cómo leer este reporte"
          class="rpt-help"
        >
          <v-icon>help_outline</v-icon>
        </v-btn>
        <v-btn
          class="rpt-btn--ghost"
          depressed
          :disabled="!detalle.length"
          @click="exportar"
        >
          <v-icon left small>file_download</v-icon>Exportar
        </v-btn>
        <v-btn class="rpt-btn" depressed :loading="loading" @click="load">
          <v-icon left small>refresh</v-icon>Actualizar
        </v-btn>
      </div>
    </div>

    <v-layout row wrap class="rpt-filters">
      <v-flex md3 sm6 xs12>
        <v-menu
          :close-on-content-click="false"
          v-model="startMenu"
          transition="scale-transition"
          offset-y
          max-width="290px"
          min-width="290px"
        >
          <v-text-field
            slot="activator"
            v-model="startDate"
            label="Desde"
            prepend-icon="event"
            readonly
          ></v-text-field>
          <v-date-picker
            v-model="startDate"
            locale="es-es"
            color="#254f87"
            no-title
            @input="startMenu = false"
          ></v-date-picker>
        </v-menu>
      </v-flex>

      <v-flex md3 sm6 xs12>
        <v-menu
          :close-on-content-click="false"
          v-model="endMenu"
          transition="scale-transition"
          offset-y
          max-width="290px"
          min-width="290px"
        >
          <v-text-field
            slot="activator"
            v-model="endDate"
            label="Hasta"
            prepend-icon="event"
            readonly
          ></v-text-field>
          <v-date-picker
            v-model="endDate"
            locale="es-es"
            color="#254f87"
            no-title
            @input="endMenu = false"
          ></v-date-picker>
        </v-menu>
      </v-flex>

      <v-flex md3 sm6 xs12>
        <v-select
          :items="laboratorioItems"
          v-model="labId"
          label="Laboratorio"
          prepend-icon="business"
          clearable
        ></v-select>
      </v-flex>

      <v-flex md3 sm6 xs12>
        <v-select
          :items="limitOptions"
          v-model="limit"
          label="Filas de detalle"
          prepend-icon="list"
        ></v-select>
      </v-flex>
    </v-layout>

    <v-layout row wrap v-if="resumen">
      <v-flex lg3 sm6 xs12>
        <div class="rpt-tile rpt-tile--hero">
          <span class="rpt-tile__label">Tasa de rechazo</span>
          <span class="rpt-tile__value">
            {{ format(resumen.pctRechazo) }}<small>%</small>
          </span>
          <span class="rpt-tile__foot">
            {{ resumen.n }} rechazadas de {{ resumen.recibidas }} recibidas
          </span>
        </div>
      </v-flex>

      <v-flex lg3 sm6 xs12>
        <div class="rpt-tile">
          <span class="rpt-tile__label">Valor rechazado (CIF)</span>
          <span class="rpt-tile__value">{{ money(resumen.cif) }}</span>
          <span class="rpt-tile__foot">monto que hubo que reprocesar</span>
        </div>
      </v-flex>

      <v-flex lg3 sm6 xs12>
        <div class="rpt-tile">
          <span class="rpt-tile__label">Laboratorios afectados</span>
          <span class="rpt-tile__value">
            {{ resumen.laboratorios }}<small>labs</small>
          </span>
          <span class="rpt-tile__foot">con al menos un rechazo</span>
        </div>
      </v-flex>

      <v-flex lg3 sm6 xs12>
        <div class="rpt-tile">
          <span class="rpt-tile__label">Sin motivo registrado</span>
          <span class="rpt-tile__value">
            {{ resumen.sinMotivo }}<small>casos</small>
          </span>
          <span class="rpt-tile__foot">
            no se puede analizar la causa raíz
          </span>
        </div>
      </v-flex>
    </v-layout>

    <v-layout row wrap>
      <v-flex lg7 sm12 xs12 v-if="chartData">
        <v-widget content-bg="white" title="Rechazos por mes">
          <div slot="widget-content">
            <e-chart
              :path-option="chartOptions"
              width="95%"
              height="300px"
            ></e-chart>
          </div>
        </v-widget>
      </v-flex>

      <v-flex lg5 sm12 xs12>
        <v-widget content-bg="white" title="Laboratorios con mayor rechazo">
          <div slot="widget-content">
            <div class="rpt-scroll">
              <table class="rpt-table">
                <thead>
                  <tr>
                    <th>Laboratorio</th>
                    <th class="num">Rech.</th>
                    <th class="num">Recib.</th>
                    <th class="num">Tasa</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(l, i) in porLaboratorio" :key="i">
                    <td>{{ l.laboratorio }}</td>
                    <td class="num rpt-strong">{{ l.rechazadas }}</td>
                    <td class="num rpt-muted">{{ l.recibidas }}</td>
                    <td class="num">
                      <span
                        :class="[
                          'rpt-pill',
                          l.pctRechazo >= 20 ? 'rpt-pill--warn' : '',
                        ]"
                      >
                        {{ format(l.pctRechazo) }}%
                      </span>
                    </td>
                  </tr>
                  <tr v-if="!porLaboratorio.length">
                    <td colspan="4" class="rpt-empty">
                      Sin rechazos en el periodo.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </v-widget>
      </v-flex>
    </v-layout>

    <v-layout row wrap>
      <v-flex lg12 sm12 xs12>
        <v-widget content-bg="white" title="Detalle de rechazos">
          <div slot="widget-content">
            <div class="rpt-scroll">
              <table class="rpt-table">
                <thead>
                  <tr>
                    <th># Factura</th>
                    <th>Laboratorio</th>
                    <th>Motivo</th>
                    <th class="num">CIF</th>
                    <th class="num">Rechazada</th>
                    <th class="num">Días</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="f in detalle" :key="f.id">
                    <td class="rpt-strong">{{ f.invoiceNumber }}</td>
                    <td class="rpt-muted">{{ f.laboratorio || '—' }}</td>
                    <td>{{ f.motivo || '—' }}</td>
                    <td class="num">{{ money(f.cif) }}</td>
                    <td class="num rpt-muted">{{ date(f.deniedDate) }}</td>
                    <td class="num">
                      <span class="rpt-pill">{{
                        format(f.diasHastaRechazo)
                      }}</span>
                    </td>
                  </tr>
                  <tr v-if="!detalle.length">
                    <td colspan="6" class="rpt-empty">Sin resultados.</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p
              v-if="meta && meta.truncado"
              class="rpt-muted"
              style="font-size:12px;margin:10px 2px 0"
            >
              Se muestran los {{ meta.limite }} rechazos más recientes. Suba el
              límite de filas para ver más.
            </p>
          </div>
        </v-widget>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import EChart from '@/components/chart/echart'
import VWidget from '@/components/VWidget'
import exportReport from '@/helpers/ReportExport'
import ReportFilters from './mixins/ReportFilters'

export default {
  components: { VWidget, EChart },

  mixins: [ReportFilters],

  data() {
    return {
      limit: 100,
      limitOptions: [50, 100, 250, 500],
      meta: null,
      resumen: null,
      tendencia: [],
      porLaboratorio: [],
      detalle: [],
      chartData: null,
      loading: false,
    }
  },

  computed: {
    chartOptions() {
      return [
        ['dataset.source', this.chartData],
        ['color', ['#BD614D']],
        ['legend.show', false],
        ['grid.left', 56],
        ['grid.right', 24],
        ['grid.top', 20],
        ['grid.bottom', 32],
        ['tooltip.trigger', 'axis'],
        ['tooltip.axisPointer.type', 'shadow'],
        ['xAxis.type', 'category'],
        // El wrapper oculta las etiquetas de eje por defecto: hay que pedirlas.
        ['xAxis.axisLabel.show', true],
        ['xAxis.axisTick.show', false],
        ['xAxis.axisLine.lineStyle.color', '#e2e8f0'],
        ['yAxis.type', 'value'],
        ['yAxis.name', 'rechazos'],
        ['yAxis.nameTextStyle.color', '#64748b'],
        ['yAxis.axisLabel.show', true],
        ['yAxis.axisLine.show', false],
        ['yAxis.axisTick.show', false],
        ['yAxis.splitLine.lineStyle.color', '#eef2f7'],
        ['series[0].type', 'bar'],
        ['series[0].barMaxWidth', 28],
        ['series[0].itemStyle.barBorderRadius', [4, 4, 0, 0]],
      ]
    },
  },

  watch: {
    labId() {
      this.load()
    },
    startDate() {
      this.load()
    },
    endDate() {
      this.load()
    },
    limit() {
      this.load()
    },
  },

  created() {
    this.loadCatalogos()
    this.load()
  },

  methods: {
    load() {
      this.chartData = null
      this.loading = true

      this.$store
        .dispatch(
          'reports/getRechazos',
          this.paramsConFechas({ limit: this.limit })
        )
        .then(response => {
          const data = response && response.data
          if (!data) return
          this.meta = data.meta
          this.resumen = data.resumen
          this.tendencia = data.tendencia || []
          this.porLaboratorio = data.porLaboratorio || []
          this.detalle = data.detalle || []
          this.chartData = this.tendencia.map(row => ({
            mes: row.label,
            Rechazos: row.n,
          }))
        })
        .finally(() => {
          this.loading = false
        })
    },

    exportar() {
      exportReport('rechazos', [
        {
          name: 'Detalle',
          rows: this.detalle,
          columns: [
            { key: 'invoiceNumber', label: 'Factura' },
            { key: 'controlNumber', label: 'Control' },
            { key: 'laboratorio', label: 'Laboratorio' },
            { key: 'distribuidor', label: 'Distribuidora' },
            { key: 'cif', label: 'CIF' },
            { key: 'receivedDate', label: 'Recibida', format: this.date },
            { key: 'deniedDate', label: 'Rechazada', format: this.date },
            { key: 'diasHastaRechazo', label: 'Días hasta rechazo' },
            { key: 'motivo', label: 'Motivo' },
          ],
        },
        {
          name: 'Por laboratorio',
          rows: this.porLaboratorio,
          columns: [
            { key: 'laboratorio', label: 'Laboratorio' },
            { key: 'recibidas', label: 'Recibidas' },
            { key: 'rechazadas', label: 'Rechazadas' },
            { key: 'pctRechazo', label: '% rechazo' },
            { key: 'cifRechazado', label: 'CIF rechazado' },
          ],
        },
        {
          name: 'Tendencia',
          rows: this.tendencia,
          columns: [
            { key: 'periodo', label: 'Periodo' },
            { key: 'n', label: 'Rechazos' },
            { key: 'cif', label: 'CIF' },
          ],
        },
      ])
    },
  },
}
</script>
